<!--
* A button component
*
* @param {string} text - Text of the button.
* @param {boolean} hasIconLeft - Whether the button has an icon on the left.
* @param {boolean} hasIconRight - Whether the button has an icon on the right.
* @param {ButtonTypes} type - Button type, e.g. primary, secondary etc
* @param {ButtonStates} state - Button state, e.g enabled, disabled etc
-->

<template>
  <div
    class="app_button"
    :class="[ typeClass, stateClass, { 'app_button--has-left-icon': hasIconLeft } ]">
    {{ text }}
    <span
      v-if="hasIconLeft || hasIconRight"
      :class="{
        'app_button__icon_left': hasIconLeft,
        'app_button__icon_right': hasIconRight
      }">
    </span>
  </div>
</template>

<script>
  /**
   * Valid button states
   */
  export const ButtonStates = {
    DISABLED: 'disabled',
    ENABLED: 'enabled'
  }

  /**
   * Valid button types
   */
  export const ButtonTypes = {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    TERTIARY: 'tertiary',
    QUATERNARY: 'quaternary',
    QUINARY: 'quinary',
    SENARY: 'senary'
  }

  export default {
    name: 'AppButton',
    props: {
      text: {
        type: String,
        required: true
      },

      hasIconLeft: {
        type: Boolean
      },

      hasIconRight: {
        type: Boolean
      },

      type: {
        type: String
      },

      state: {
        type: String
      }
    },

    computed: {
      /**
       * Set custom button type class if type prop is available
       * @returns {object}  Object to bind as class
       */
      typeClass: function () {
        return {
          ['app_button--type-' + this.type]: this.type
        }
      },

      /**
       * Set custom button state class if state prop is available
       * @returns {object}  Object to bind as class
       */
      stateClass: function () {
        return {
          ['app_button--state-' + this.state]: this.state
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "../assets/sass/shared.scss";

  $button-font-weight: $bold-font-weight;
  $button-font-size: $medium-font-size;
  $button-icon-horizontal-margin: $dims-padding-default;
  $button-icon-size: $select-arrow-width;
  $button-transition: all $default-transition-duration $default-easing;

  .app_button {
    user-select: none;
    cursor: pointer;
    border: 1px solid;
    transition: $button-transition;
    font-weight: $button-font-weight;
    font-size: $button-font-size;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .app_button--has-left-icon {
    flex-direction: row-reverse;
  }

  .app_button__icon_left,
  .app_button__icon_right {
    display: inline-block;
    width: $button-icon-size;
    height: $button-icon-size;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
  }

  .app_button__icon_left {
    float: left;
    margin-right: $button-icon-horizontal-margin;
  }

  .app_button__icon_right {
    float: right;
    margin-left: $button-icon-horizontal-margin;
  }

  .app_button--type-primary {
    background-color: $primary-button-background;
    color: $primary-button-color;
    border-color: $primary-button-background;

    .app_button__icon_left,
    .app_button__icon_right {
      background-image: url($primary-button-icon-src);
    }

    &:hover {
      background-color: $primary-button-hover-background
    }
  }

  .app_button--type-secondary {
    background-color: $secondary-button-background;
    color: $secondary-button-color;
    border-color: $secondary-button-border-color;

    &:hover {
      background-color: $secondary-button-hover-background;
      color: $secondary-button-hover-color;
      border-color: $secondary-button-hover-color;
    }
  }

  .app_button--type-tertiary {
    background-color: $tertiary-button-background;
    color: $tertiary-button-color;

    .app_button__icon_left,
    .app_button__icon_right {
      background-image: url($tertiary-button-icon-src);
    }

    &:hover {
      background-color: $tertiary-button-hover-background;
      color: $tertiary-button-hover-color;
      border-color: $tertiary-button-hover-color;

      .app_button__icon_left,
      .app_button__icon_right {
        background-image: url($tertiary-button-icon-hover-src);
      }
    }
  }

  .app_button--type-quaternary {
    background-color: $quaternary-button-background;
    color: $quaternary-button-color;

    .app_button__icon_left,
    .app_button__icon_right {
      background-image: url($quaternary-button-icon-src);
    }

    &:hover {
      background-color: $quaternary-button-hover-background;
      color: $quaternary-button-hover-color;
      border-color: $quaternary-button-hover-color;

      .app_button__icon_left,
      .app_button__icon_right {
        background-image: url($quaternary-button-icon-hover-src);
      }
    }
  }

  .app_button--type-quinary {
    background-color: $quinary-button-background;
    color: $quinary-button-color;

    .app_button__icon_left,
    .app_button__icon_right {
      background-image: url($quinary-button-icon-src);
    }

    &:hover {
      background-color: $quinary-button-hover-background;
      color: $quinary-button-hover-color;
      border-color: $quinary-button-hover-color;

      .app_button__icon_left,
      .app_button__icon_right {
        background-image: url($quinary-button-icon-hover-src);
      }
    }
  }

  .app_button--type-senary {
    background-color: $senary-button-background;
    color: $senary-button-color;
    border-color: $senary-button-hover-color;

    .app_button__icon_left,
    .app_button__icon_right {
      background-image: url($senary-button-icon-src);
      // 2x bigger size here
      width: $button-icon-size * 2;
      height: $button-icon-size * 2;
  }

  .app_button__icon_left {
    // 2x smaller gap
    margin-right: $button-icon-horizontal-margin / 2;
  }

  .app_button__icon_right {
    // 2x smaller gap
    margin-left: $button-icon-horizontal-margin / 2;
  }
  
    &:hover {
      background-color: $senary-button-hover-background;
      color: $senary-button-hover-color;
      border-color: $senary-button-hover-color;

      .app_button__icon_left,
      .app_button__icon_right {
        background-image: url($senary-button-icon-hover-src);
      }
    }
  }

  .app_button--state-disabled {
    opacity: $button-disabled-opacity;
    pointer-events: none;
  }

</style>
